/* eslint-disable react-refresh/only-export-components */
import React, { useMemo } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-cartesian-dist';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';

// Use the smaller cartesian-only Plotly bundle (~1MB instead of ~3MB)
const Plot = createPlotlyComponent(Plotly);

// Dark theme configuration matching our app
const darkTheme = {
  paper_bgcolor: 'rgba(26, 26, 46, 0.95)',
  plot_bgcolor: 'rgba(26, 26, 46, 0.95)',
  font: {
    color: 'rgba(255, 255, 255, 0.8)',
    family: 'Inter, sans-serif',
  },
  xaxis: {
    gridcolor: 'rgba(255, 255, 255, 0.1)',
    linecolor: 'rgba(255, 255, 255, 0.2)',
    tickfont: { color: 'rgba(255, 255, 255, 0.7)' },
    title: { font: { color: 'rgba(255, 255, 255, 0.8)' } },
    zerolinecolor: 'rgba(255, 255, 255, 0.2)',
  },
  yaxis: {
    gridcolor: 'rgba(255, 255, 255, 0.1)',
    linecolor: 'rgba(255, 255, 255, 0.2)',
    tickfont: { color: 'rgba(255, 255, 255, 0.7)' },
    title: { font: { color: 'rgba(255, 255, 255, 0.8)' } },
    zerolinecolor: 'rgba(255, 255, 255, 0.2)',
  },
  colorway: ['#00d4ff', '#00ff88', '#ff6b6b', '#f1c40f', '#9b59b6', '#3498db', '#e74c3c', '#2ecc71'],
  hoverlabel: {
    bgcolor: '#1a1a2e',
    bordercolor: 'rgba(255, 255, 255, 0.2)',
    font: { color: 'white' },
  },
};

// Common config for all charts
const defaultConfig = {
  displayModeBar: true,
  displaylogo: false,
  responsive: true,
  modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  modeBarButtonsToAdd: [],
  toImageButtonOptions: {
    format: 'png',
    filename: 'energycosmos_chart',
    height: 600,
    width: 1200,
    scale: 2,
  },
};

/**
 * PlotlyChart - A reusable Plotly chart component with dark theme and interactive features
 *
 * @param {Object} props
 * @param {Array} props.data - Plotly data array
 * @param {Object} props.layout - Plotly layout object (merged with defaults)
 * @param {Object} props.config - Plotly config object (merged with defaults)
 * @param {string} props.title - Chart title
 * @param {number} props.height - Chart height in pixels
 * @param {boolean} props.showRangeSlider - Show range slider for x-axis
 * @param {boolean} props.showLegend - Show legend
 * @param {string} props.xAxisTitle - X-axis title
 * @param {string} props.yAxisTitle - Y-axis title
 * @param {Function} props.onFullscreen - Callback when fullscreen button clicked
 */
export default function PlotlyChart({
  data,
  layout: customLayout = {},
  config: customConfig = {},
  title,
  height = 400,
  showRangeSlider = false,
  showLegend = true,
  xAxisTitle,
  yAxisTitle,
  onFullscreen,
  onClick,
  style = {},
}) {
  const mergedLayout = useMemo(() => {
    const baseLayout = {
      ...darkTheme,
      autosize: true,
      height,
      margin: { l: 60, r: 30, t: title ? 40 : 20, b: showRangeSlider ? 80 : 50 },
      showlegend: showLegend,
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'right',
        x: 1,
        font: { color: 'rgba(255, 255, 255, 0.8)' },
        bgcolor: 'transparent',
      },
      hovermode: 'x unified',
      dragmode: 'zoom', // Enable drag-to-zoom by default
      xaxis: {
        ...darkTheme.xaxis,
        title: xAxisTitle ? { text: xAxisTitle, font: { color: 'rgba(255, 255, 255, 0.8)' } } : undefined,
        rangeslider: showRangeSlider ? { visible: true, bgcolor: 'rgba(26, 26, 46, 0.8)' } : { visible: false },
        rangeselector: showRangeSlider ? {
          buttons: [
            { count: 6, label: '6 SP', step: 'all', stepmode: 'backward' },
            { count: 12, label: '12 SP', step: 'all', stepmode: 'backward' },
            { count: 24, label: '24 SP', step: 'all', stepmode: 'backward' },
            { step: 'all', label: 'All' },
          ],
          bgcolor: 'rgba(26, 26, 46, 0.8)',
          activecolor: '#00d4ff',
          bordercolor: 'rgba(255, 255, 255, 0.2)',
          font: { color: 'rgba(255, 255, 255, 0.8)' },
        } : undefined,
      },
      yaxis: {
        ...darkTheme.yaxis,
        title: yAxisTitle ? { text: yAxisTitle, font: { color: 'rgba(255, 255, 255, 0.8)' } } : undefined,
        fixedrange: false, // Allow y-axis zoom
      },
    };

    // Merge custom layout
    return {
      ...baseLayout,
      ...customLayout,
      xaxis: { ...baseLayout.xaxis, ...customLayout.xaxis },
      yaxis: { ...baseLayout.yaxis, ...customLayout.yaxis },
    };
  }, [customLayout, height, title, showRangeSlider, showLegend, xAxisTitle, yAxisTitle]);

  const mergedConfig = useMemo(() => ({
    ...defaultConfig,
    ...customConfig,
    toImageButtonOptions: {
      ...defaultConfig.toImageButtonOptions,
      filename: title ? `energycosmos_${title.toLowerCase().replace(/\s+/g, '_')}` : 'energycosmos_chart',
      ...customConfig.toImageButtonOptions,
    },
  }), [customConfig, title]);

  return (
    <Box sx={{ position: 'relative', ...style }}>
      {title && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
            {title}
          </Typography>
          {onFullscreen && (
            <Tooltip title="Fullscreen">
              <IconButton size="small" onClick={onFullscreen} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Box
        sx={{
          '& .modebar': {
            backgroundColor: 'transparent !important',
          },
          '& .modebar-btn': {
            color: 'rgba(255, 255, 255, 0.5) !important',
            '&:hover': {
              color: '#00d4ff !important',
            },
          },
          '& .modebar-btn.active': {
            color: '#00d4ff !important',
          },
        }}
      >
        <Plot
          data={data}
          layout={mergedLayout}
          config={mergedConfig}
          style={{ width: '100%', height: `${height}px` }}
          useResizeHandler
          onClick={onClick}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.4)',
          display: 'block',
          textAlign: 'center',
          mt: 0.5,
          fontSize: '0.7rem',
        }}
      >
        Drag to zoom • Double-click to reset • Use toolbar to pan/export
      </Typography>
    </Box>
  );
}

/**
 * Helper function to create a line trace
 */
export function createLineTrace(x, y, name, color, options = {}) {
  return {
    x,
    y,
    type: 'scatter',
    mode: 'lines+markers',
    name,
    line: { color, width: 2, ...options.line },
    marker: { size: 4, color, ...options.marker },
    hovertemplate: options.hovertemplate || `<b>${name}</b><br>%{x}<br>%{y:.2f}<extra></extra>`,
    ...options,
  };
}

/**
 * Helper function to create a bar trace
 */
export function createBarTrace(x, y, name, color, options = {}) {
  return {
    x,
    y,
    type: 'bar',
    name,
    marker: { color, ...options.marker },
    hovertemplate: options.hovertemplate || `<b>${name}</b><br>%{x}<br>%{y:.2f}<extra></extra>`,
    ...options,
  };
}

/**
 * Helper function to create an area trace
 */
export function createAreaTrace(x, y, name, color, options = {}) {
  return {
    x,
    y,
    type: 'scatter',
    mode: 'lines',
    name,
    fill: 'tozeroy',
    fillcolor: color.replace(')', ', 0.3)').replace('rgb', 'rgba'),
    line: { color, width: 2, ...options.line },
    hovertemplate: options.hovertemplate || `<b>${name}</b><br>%{x}<br>%{y:.2f}<extra></extra>`,
    ...options,
  };
}
